import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Coffee,
  LogIn,
  LogOut,
  MapPin,
  Pause,
  Play,
  Square,
  type LucideIcon,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import BottomNav from "../components/BottomNav";
import CameraCapture from "../components/CameraCapture";
import { useAuth } from "../context/AuthContext";
import { useDeviceFingerprint } from "../hooks/useDeviceFingerprint";
import type {
  ClockEventOut,
  ClockEventSummary,
  ClockStatus,
  ClockStatusResponse,
  EventType,
} from "../types";
import { formatCoordinates } from "../utils/formatters";
import { captureGeolocation } from "../utils/permissions";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  day: "numeric",
  month: "short",
});

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const EVENT_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

function formatDuration(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatMmSs(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(Math.abs(totalSeconds)));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function eventLabel(type: EventType): string {
  switch (type) {
    case "clock_in":
      return "Checked in";
    case "break_start":
      return "Break started";
    case "break_end":
      return "Back from break";
    case "clock_out":
      return "Checked out";
  }
}

function eventIcon(type: EventType): LucideIcon {
  switch (type) {
    case "clock_in":
      return Play;
    case "break_start":
      return Pause;
    case "break_end":
      return Play;
    case "clock_out":
      return Square;
  }
}

/** Which action is valid from which clock state (mirrors the server rules). */
const ALLOWED_FROM: Record<EventType, ClockStatus> = {
  clock_in: "not_clocked_in",
  break_start: "clocked_in",
  clock_out: "clocked_in",
  break_end: "on_break",
};

export default function ClockScreen() {
  const { t } = useTranslation();
  const { employee, isKiosk, logout } = useAuth();
  const deviceFingerprint = useDeviceFingerprint();

  const [status, setStatus] = useState<ClockStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  const [pendingType, setPendingType] = useState<EventType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState<"success" | null>(null);
  const [gpsWarning, setGpsWarning] = useState<string | null>(null);
  const [lastLocation, setLastLocation] = useState<string | null>(null);
  const [nextBreakNotice, setNextBreakNotice] = useState<string | null>(null);
  const [kioskCountdown, setKioskCountdown] = useState<number | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const s = await api.get<ClockStatusResponse>("/api/clock/status", {
        signal: controller.signal,
      });
      setStatus(s);
      // Let the rest of the app (nav dot, Home card) know the live state.
      window.dispatchEvent(new CustomEvent("pme-clock-status", { detail: s.status }));
      setLoadError(null);
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        setLoadError((err as Error).message || "Could not load your check-in status.");
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Seconds are only shown while a break is being timed.
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const actionLabel = (type: EventType): string => {
    switch (type) {
      case "clock_in":
        return t("clock.clockIn");
      case "break_start":
        return t("clock.startBreak");
      case "break_end":
        return t("clock.endBreak");
      case "clock_out":
        return t("clock.clockOut");
    }
  };

  // The home page links here with ?start=<action> so one tap there opens
  // the camera straight away. Only honoured when the action is valid for the
  // current state; the parameter is removed either way so a refresh or the
  // back button cannot trigger it again.
  useEffect(() => {
    const start = searchParams.get("start") as EventType | null;
    if (!start || !status) return;
    if (start in ALLOWED_FROM && ALLOWED_FROM[start] === status.status) {
      setPendingType(start);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("start");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, status]);

  // Kiosk: auto-return to the login screen 5 seconds after clock-out.
  useEffect(() => {
    if (kioskCountdown == null) return;
    if (kioskCountdown <= 0) {
      void logout();
      return;
    }
    const id = window.setTimeout(
      () => setKioskCountdown((c) => (c == null ? null : c - 1)),
      1000,
    );
    return () => window.clearTimeout(id);
  }, [kioskCountdown, logout]);

  const shiftSeconds = useMemo(() => {
    if (!status?.clocked_in_at) return 0;
    return Math.max(
      0,
      Math.floor((now.getTime() - new Date(status.clocked_in_at).getTime()) / 1000),
    );
  }, [status?.clocked_in_at, now]);

  const breakSeconds = useMemo(() => {
    if (!status?.break_started_at) return 0;
    return Math.max(
      0,
      Math.floor((now.getTime() - new Date(status.break_started_at).getTime()) / 1000),
    );
  }, [status?.break_started_at, now]);

  // On-break timer: positive = remaining, negative = overage.
  const breakAllowedSeconds = status?.current_break_duration_minutes
    ? status.current_break_duration_minutes * 60
    : null;
  const breakRemainingSeconds =
    breakAllowedSeconds != null ? breakAllowedSeconds - breakSeconds : null;

  const submitClockEvent = async (eventType: EventType, photoBlob: Blob) => {
    setSubmitting(true);
    setGpsWarning(null);
    setLastLocation(null);
    setNextBreakNotice(null);
    try {
      const geo = await captureGeolocation();
      if (geo.failed) {
        setGpsWarning(t("clock.locationUnavailable"));
      } else {
        setLastLocation(formatCoordinates(geo.latitude, geo.longitude));
      }

      const created = await api.post<ClockEventOut>("/api/clock/event", {
        event_type: eventType,
        latitude: geo.latitude,
        longitude: geo.longitude,
        gps_accuracy_m: geo.gps_accuracy_m,
        device_fingerprint: deviceFingerprint,
      });

      if (
        eventType === "break_start" &&
        created.break_rule_name &&
        created.break_rule_duration_minutes
      ) {
        setNextBreakNotice(
          t("clock.startingBreak", {
            name: created.break_rule_name,
            minutes: created.break_rule_duration_minutes,
          }),
        );
      }

      const form = new FormData();
      form.append("file", photoBlob, `clock-${created.id}.jpg`);
      try {
        await api.putFormData(`/api/clock/event/${created.id}/photo`, form);
      } catch (err) {
        console.error("photo upload failed", err);
      }

      setFlash("success");
      window.setTimeout(() => setFlash(null), 1200);
      await refresh();

      if (isKiosk && eventType === "clock_out") {
        setKioskCountdown(5);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setLoadError(err.message || "Could not record that. Please try again.");
      } else {
        setLoadError("Could not record that. Please try again.");
      }
    } finally {
      setSubmitting(false);
      setPendingType(null);
    }
  };

  const state: ClockStatus = status?.status ?? "not_clocked_in";

  const statusPill = (() => {
    switch (state) {
      case "clocked_in":
        return { text: t("clock.onShift"), class: "bg-green-100 text-green-800" };
      case "on_break":
        return { text: t("clock.onBreak"), class: "bg-yellow-100 text-yellow-800" };
      default:
        return { text: t("clock.offShift"), class: "bg-gray-100 text-gray-700" };
    }
  })();

  return (
    <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col">
      <header className="safe-top px-5 pb-3 pt-4">
        <div className="flex items-center gap-3 pr-28">
          {!isKiosk && (
            <Link
              to="/home"
              aria-label={t("nav.backToHome")}
              title={t("nav.backToHome")}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-pme-line bg-white text-pme-navy hover:bg-pme-sky/50"
            >
              <ArrowLeft size={18} strokeWidth={2} aria-hidden />
            </Link>
          )}
          <div className="min-w-0">
            <p className="truncate text-base font-bold text-pme-navy">
              {employee ? employee.name : t("common.loading")}
            </p>
            <p className="truncate text-xs text-pme-muted">
              {employee ? `${employee.employee_number} · ${employee.site_name}` : ""}
            </p>
          </div>
        </div>
      </header>

      <section className="px-5 pb-2">
        <div className="card px-5 pb-5 pt-5 text-center">
          <div className="flex items-center justify-between">
            <span
              className={[
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold",
                statusPill.class,
              ].join(" ")}
            >
              <span
                aria-hidden
                className={[
                  "inline-block h-2 w-2 rounded-full",
                  state === "clocked_in"
                    ? "live-dot bg-emerald-500 text-emerald-500"
                    : state === "on_break"
                      ? "live-dot bg-amber-500 text-amber-500"
                      : "bg-gray-400",
                ].join(" ")}
              />
              {statusPill.text}
            </span>
            <span className="text-xs font-medium text-pme-muted">{DATE_FMT.format(now)}</span>
          </div>
          <p className="mt-5 text-sm font-medium text-pme-muted">
            {state === "clocked_in"
              ? t("clock.msgOnShift")
              : state === "on_break"
                ? t("clock.msgOnBreak")
                : t("clock.msgOffShift")}
          </p>
          <div className="mt-1 text-5xl font-bold leading-none text-pme-navy tabular-nums">
            {TIME_FMT.format(now)}
          </div>
          <div className="mt-3 flex items-center justify-center gap-4 text-sm text-pme-muted empty:hidden">
            {state !== "not_clocked_in" && (
              <span>
                {t("clock.shiftDuration")}: {formatDuration(shiftSeconds)}
              </span>
            )}
            {state === "on_break" && (
              <span>
                {t("clock.breakDuration")}: {formatDuration(breakSeconds)}
              </span>
            )}
          </div>
          {state === "on_break" && breakRemainingSeconds != null && (
            <p
              className={[
                "mt-2 text-sm font-semibold tabular-nums",
                breakRemainingSeconds >= 0 ? "text-pme-ink" : "text-amber-700",
              ].join(" ")}
            >
              {breakRemainingSeconds >= 0
                ? t("clock.breakRemaining", {
                    time: formatMmSs(breakRemainingSeconds),
                  })
                : t("clock.breakOverage", {
                    time: formatMmSs(breakRemainingSeconds),
                  })}
            </p>
          )}
          {status?.current_break_rule_name && state === "on_break" && (
            <p className="mt-1 text-xs text-gray-400">
              {status.current_break_rule_name}
              {status.current_break_duration_minutes
                ? ` · ${status.current_break_duration_minutes} min`
                : ""}
            </p>
          )}
        </div>
      </section>

      <section className="flex flex-col items-center gap-3 px-5 py-4">
        {loadError && (
          <p role="alert" className="text-sm text-red-600">
            {loadError}
          </p>
        )}
        {gpsWarning && (
          <p className="inline-flex items-center gap-1.5 text-xs text-pme-muted">
            <MapPin size={13} strokeWidth={1.75} aria-hidden />
            {gpsWarning}
          </p>
        )}
        {lastLocation && !gpsWarning && (
          <p className="inline-flex items-center gap-1.5 text-xs text-pme-muted">
            <MapPin size={13} strokeWidth={1.75} aria-hidden />
            {lastLocation}
          </p>
        )}
        {nextBreakNotice && (
          <p className="text-xs text-pme-blue">{nextBreakNotice}</p>
        )}

        {state === "not_clocked_in" && (
          <BigButton
            colour="bg-pme-blue text-white shadow-pop hover:bg-[#0a66ab]"
            onClick={() => setPendingType("clock_in")}
            disabled={submitting}
          >
            <LogIn size={20} strokeWidth={2} aria-hidden />
            {t("clock.clockIn")}
          </BigButton>
        )}

        {state === "clocked_in" && (
          <div className="grid w-full grid-cols-2 gap-3">
            <BigButton
              colour="border border-pme-line bg-white text-pme-navy hover:bg-pme-sky/40"
              onClick={() => setPendingType("break_start")}
              disabled={submitting}
            >
            <Coffee size={20} strokeWidth={2} aria-hidden />
            {t("clock.startBreak")}
          </BigButton>
            <BigButton
              colour="bg-pme-navy text-white hover:bg-[#0a3253]"
              onClick={() => setPendingType("clock_out")}
              disabled={submitting}
            >
            <LogOut size={20} strokeWidth={2} aria-hidden />
            {t("clock.clockOut")}
          </BigButton>
          </div>
        )}

        {state === "on_break" && (
          <BigButton
            colour="bg-pme-blue text-white shadow-pop hover:bg-[#0a66ab]"
            onClick={() => setPendingType("break_end")}
            disabled={submitting}
          >
            <Play size={20} strokeWidth={2} aria-hidden />
            {t("clock.endBreak")}
          </BigButton>
        )}
      </section>

      <section className="flex-1 overflow-y-auto px-5 pb-24 pt-2">
        <h2 className="eyebrow mb-2">
          {t("clock.todayActivity")}
        </h2>
        {status && status.todays_events.length > 0 ? (
          <ul className="space-y-2">
            {[...status.todays_events]
              .reverse()
              .map((ev) => (
                <ActivityRow key={ev.id} ev={ev} />
              ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">No events yet today.</p>
        )}
      </section>

      {flash === "success" && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 text-white">
            <Check size={40} strokeWidth={2.5} aria-label={t("clock.success")} />
          </div>
        </div>
      )}

      {pendingType && (
        <CameraCapture
          title={actionLabel(pendingType)}
          onCancel={() => {
            if (!submitting) setPendingType(null);
          }}
          onCapture={(blob) => {
            void submitClockEvent(pendingType, blob);
          }}
        />
      )}

      {kioskCountdown != null && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-black/70 text-white">
          <div className="text-center">
            <p className="text-2xl font-semibold">{t("clock.goodbye")}</p>
            <p className="mt-2 text-sm text-gray-200">
              Returning to login in {kioskCountdown}...
            </p>
          </div>
        </div>
      )}

      </div>
      {!isKiosk && <BottomNav />}
    </div>
  );
}

function BigButton({
  children,
  colour,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  colour: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "flex min-h-[56px] w-full items-center justify-center gap-2 rounded-full text-base font-semibold transition",
        "active:scale-[0.98] focus:outline-none focus-visible:ring-4 focus-visible:ring-pme-blue/30 focus-visible:ring-offset-2",
        colour,
        disabled ? "opacity-60" : "",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function ActivityRow({ ev }: { ev: ClockEventSummary }) {
  const time = EVENT_TIME_FMT.format(new Date(ev.timestamp));
  const Icon = eventIcon(ev.event_type);
  const location = formatCoordinates(ev.latitude, ev.longitude);
  return (
    <li className="flex items-center gap-3 rounded-2xl border border-pme-line/70 bg-white px-3 py-2.5 shadow-card">
      <span
        aria-hidden
        className="flex h-9 w-9 items-center justify-center rounded-full bg-pme-sky text-pme-blue"
      >
        <Icon size={15} strokeWidth={2} />
      </span>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{eventLabel(ev.event_type)}</p>
        <p className="text-xs text-gray-500">{time}</p>
        {location && (
          <p className="text-[11px] text-gray-400">Location: {location}</p>
        )}
        {ev.break_overtime_minutes != null && ev.break_overtime_minutes > 0 && (
          <p className="text-[11px] text-amber-700">
            +{ev.break_overtime_minutes} min over allowed
          </p>
        )}
      </div>
      {ev.has_photo && (
        <img
          src={`/api/clock/event/${ev.id}/photo`}
          alt=""
          className="h-10 w-10 rounded-full object-cover"
        />
      )}
    </li>
  );
}
